import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { stagesFor, SHIPMENT_TYPES, stageLabel } from "@/lib/stages";
import { useAuth } from "@/context/AuthContext";
import { Package, Users as UsersIcon, Activity, Receipt, ArrowRight, AlertTriangle } from "lucide-react";
import { AogTag } from "@/components/shipment/AogTag";
import { DueChecksPanel } from "@/components/DueChecksPanel";

const AogPanel = ({ list }) => (
  <div data-testid="aog-panel" className="rounded-lg p-5 border border-rose-500/40 bg-rose-950/30">
    <div className="flex items-center gap-2 mb-3">
      <AlertTriangle size={18} className="text-rose-400" />
      <h2 className="font-display text-xl font-bold text-rose-100">AOG: handle first</h2>
      <span data-testid="aog-count" className="ml-1 text-xs font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white">{list.length}</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
      {list.map((s) => (
        <Link key={s.id} to={`/shipments/${s.id}`} data-testid={`aog-shipment-${s.id}`}
              className="block bg-slate-950/60 border border-rose-500/30 rounded-md p-3 hover:border-rose-400 transition-colors">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono-ops text-sm text-sky-300">{s.shipment_number}</span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">{s.type || "brokerage"} · {stageLabel(s.current_stage)}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1 truncate">
            {s.documentation?.mawb_number || s.pre_alert?.mawb_hint || "—"} · {s.documentation?.client_name || s.pre_alert?.client_hint || s.pre_alert?.sender_name || "—"}
          </div>
        </Link>
      ))}
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, hint, testid, accent = "text-sky-300" }) => (
  <div data-testid={testid} className="glass-card rounded-lg p-5 hover:border-slate-600 transition-colors">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
      <Icon size={18} className={accent} />
    </div>
    <div className="font-display text-4xl font-bold mt-2">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Overview</div>
        <h1 className="font-display text-4xl font-bold">Welcome back, {user?.name}</h1>
        <p className="text-slate-400 text-sm mt-1">Operational pulse across all shipment stages.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-total-shipments" icon={Package} label="Total Shipments" value={data.total_shipments} />
        <StatCard testid="stat-unpaid-invoices" icon={Receipt} label="Unpaid Invoices" value={data.unpaid_invoices} accent="text-amber-300" />
        <StatCard testid="stat-active-users" icon={UsersIcon} label="Portal Users" value={data.user_count} accent="text-emerald-300" />
        <StatCard testid="stat-active-pipeline" icon={Activity} label="Active Pipeline" value={data.active} hint="Not yet acknowledged by billing" accent="text-rose-300" />
      </div>

      {data.my_to_liquidate?.length > 0 && (
        <div data-testid="my-liquidations-panel" className="rounded-lg p-5 border border-amber-500/40 bg-amber-950/20">
          <h2 className="font-display text-xl font-bold text-amber-100 mb-3">My cash advances to liquidate</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {data.my_to_liquidate.map((f) => (
              <Link key={f.id} to={`/shipments/${f.shipment_id}?tab=liquidation`} data-testid={`my-liq-${f.number}`}
                    className="block bg-slate-950/60 border border-amber-500/30 rounded-md p-3 hover:border-amber-400 transition-colors">
                <div className="flex justify-between"><span className="font-mono-ops text-sm text-sky-300">{f.shipment_number}</span>
                  <span className="font-mono-ops text-sm text-slate-200">{Number(f.amount).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span></div>
                <div className="text-xs text-slate-400 mt-1">{f.number} · open the Liquidation tab</div>
              </Link>
            ))}
          </div>
        </div>
      )}
      {data.due_checks?.length > 0 && <DueChecksPanel list={data.due_checks} />}
      {data.aog_open?.length > 0 && <AogPanel list={data.aog_open} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl font-bold">Shipments by Stage</h2>
            <Link to="/shipments" className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-5">
            {SHIPMENT_TYPES.map(([type, label]) => (
              <div key={type}>
                <Link to={`/shipments?type=${type}`} className="text-xs uppercase tracking-widest text-sky-400 hover:text-sky-300">{label}</Link>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                  {stagesFor(type).map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.key} data-testid={`stage-count-${type}-${s.key}`}
                           className="bg-slate-900/60 border border-slate-800 rounded-md p-3 hover:border-slate-600 transition-colors">
                        <div className="flex items-center justify-between text-slate-400">
                          <span className="text-[10px] uppercase tracking-widest">{s.label}</span>
                          <Icon size={14} />
                        </div>
                        <div className="font-display text-3xl font-bold mt-1">{data.by_type?.[type]?.[s.key] || 0}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card rounded-lg p-6">
          <h2 className="font-display text-xl font-bold mb-5">Recent Activity</h2>
          <div className="space-y-3">
            {data.recent_shipments.length === 0 && (
              <div className="text-sm text-slate-500">No shipments yet.</div>
            )}
            {data.recent_shipments.map((s) => (
              <Link key={s.id} to={`/shipments/${s.id}`} data-testid={`recent-shipment-${s.id}`}
                    className="block bg-slate-900/60 border border-slate-800 rounded-md p-3 hover:border-sky-500/40 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-ops text-sm text-sky-300">{s.shipment_number}</span>
                    <AogTag s={s} testid={`recent-aog-${s.id}`} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">{s.type || "brokerage"} · {stageLabel(s.current_stage)}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1 truncate">
                  {s.documentation?.client_name || s.pre_alert?.sender_name || "—"}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
